import type { ObjectId } from "@novorender/data-js-api";
import type { ReadonlyVec3 } from "gl-matrix";

import { procoreClient } from "./client";
import { throwWhenBackfilling } from "./errors";
import type { SearchPredicate } from "./searchPredicate";

/**
 * Procore's **BIM Query API** — the bim-index engine served over HTTP.
 *
 * The same index `features/bimIndex` reads out of OPFS, answered by a service that has
 * the files mounted instead. It is published under `/rest/v2.1` rather than the `v2.0`
 * the rest of the BIM API uses, to separate the index-backed query surface from the
 * endpoints it replaces — the `v2.0` `search`/`objects`/`resolve` trio in
 * `bimObjectSearch.ts`, `bimObjectMetadata.ts` and `bimObjectResolve.ts`, which
 * bim-search-db still serves.
 *
 * @see https://developers.procore.com/reference/rest/bim-objects
 * @see procore/bim-platform-service `src/Procore.Bim.Platform.Api/Features/BimQuery`
 *
 * Two request-wide choices are made here rather than exposed:
 *
 * - **`id_kind=INTERNAL`.** The API addresses an object either by its 32-char hex
 *   Procore/InstanceId or by the engine's own `u32`, and the latter is the viewer's
 *   `ObjectId` — so internal ids are both what every caller of this module already
 *   holds and the cheaper walk (no external-id lookup in, no batch resolution out).
 *   The API's own default is `EXTERNAL`, so every request states it.
 * - **`view`**, which decides how much of each row comes back: bare ids (`ID`),
 *   id/path/name/`has_children` (`LIGHT`), or those plus properties (`FULL`). Each
 *   function below asks for the least its caller needs.
 *
 * Every read can answer `202` with a pending body when the scene version's index is not
 * yet on the service's mount, so all of them map it to `SceneBackfillingError` through
 * {@link throwWhenBackfilling} — {@link probeBimQuery} being the one deliberate
 * exception, for the reason given there.
 *
 * Every function takes the {@link BimQueryTarget} it addresses as its own first argument,
 * separately from what the call itself asks for. The target is fixed for a whole session
 * while the rest changes per request, so keeping them apart lets a caller hold one and
 * spread neither.
 */

/** Which scene version a BIM Query read addresses. */
export interface BimQueryTarget {
    companyId: number;
    projectId: number;
    /** The Procore scene id. The route constrains this segment to a guid. */
    sceneId: string;
    /** A scene version id, or the literal `current`. */
    sceneVersionId: string;
}

/**
 * The id segment that names the virtual super-root above the top-level nodes, so
 * listing the top of the tree is the same call as expanding any other node. It cannot
 * collide with a real id: internal ids are decimal digits.
 */
export const BIM_QUERY_ROOT = "root";

/** Largest `per_page` the tree navigation reads accept (the framework's default cap). */
export const BIM_QUERY_TREE_PAGE_LIMIT = 100;

/**
 * Largest `per_page` the bulk object reads accept — search and descendants, which are
 * raised above the framework's 100 because their typical caller wants the whole set.
 */
export const BIM_QUERY_OBJECT_PAGE_LIMIT = 1000;

/**
 * How many ids {@link expandBimQueryObjectIds} sends in one request.
 *
 * **Ours, not the API's.** The endpoint places no ceiling on the list: it is bounded by
 * the maximum request body size, past which the framework answers `413` rather than
 * anything a caller can act on. So the client picks a width that cannot reach that
 * bound. An internal id is at most ten digits plus a separator, which puts this at
 * roughly 550 KB of body — comfortably inside a default limit and inside the 1 MB an
 * intermediary is most likely to impose.
 *
 * Raise it only against a known configured limit. Nothing breaks at a smaller value
 * except request count, since the union of the batches is the union of the whole.
 */
export const BIM_QUERY_EXPAND_ID_LIMIT = 50_000;

/**
 * How many ids {@link getBimQueryBounds} sends in one request.
 *
 * {@link BIM_QUERY_EXPAND_ID_LIMIT}'s reasoning exactly — the endpoint places no ceiling
 * on the id list either, so the client picks a width that cannot reach the request-body
 * bound. Separate from it only so that raising one against a measured limit does not
 * silently move the other.
 */
export const BIM_QUERY_BOUNDS_ID_LIMIT = 50_000;

/** One node of the object tree: a parser's object, or a grouping row above them. */
export interface BimQueryTreeNode {
    id: ObjectId;
    /** Display name — the leaf segment of the node's path. The API sends no path here. */
    name: string;
    /** Objects anywhere in this node's subtree, the node included. */
    descendantCount: number;
    hasChildren: boolean;
}

/** Bounding volume of an object, in the shape `ObjectData.bounds` wants. */
export interface BimQueryBounds {
    box: { min: ReadonlyVec3; max: ReadonlyVec3 };
    sphere: { center: ReadonlyVec3; radius: number };
}

/** Which enclosing shape(s) a bounds read computes. `BOX` is the API's own default. */
export type BimQueryBoundsParts = "BOX" | "SPHERE" | "BOTH";

/**
 * The volume enclosing a selection — a *computed* fold, so unlike {@link BimQueryBounds}
 * both halves are optional: only the part(s) the request asked for are populated, and
 * each is folded independently over the selection's members, so neither necessarily
 * encloses the other.
 */
export interface BimQueryComputedBounds {
    box?: { min: ReadonlyVec3; max: ReadonlyVec3 };
    sphere?: { center: ReadonlyVec3; radius: number };
}

/** One object of the index. `properties` is populated only for a `FULL` read. */
export interface BimQueryObject {
    id: ObjectId;
    /** Full `/`-delimited path in the scene hierarchy. */
    path: string;
    /** The leaf segment of {@link path}. */
    name: string;
    /** Depth in the tree, 0 at the root. */
    level: number;
    /**
     * Whether the object has children in the hierarchy — a grouping node rather than a
     * leaf. The same flag the tree rows carry, on the object itself, so a row that never
     * came from the tree still knows its own node type. Sent for `LIGHT` and `FULL`
     * alike, which is every read that answers objects rather than bare ids.
     */
    hasChildren: boolean;
    bounds?: BimQueryBounds;
    properties: [string, string][];
}

/** Always internal ids — see the module comment. */
const ID_KIND = "INTERNAL";

type Envelope<T> = { data: T; meta?: { degraded?: boolean } };

/**
 * Integers wide enough to need it are declared `["integer", "string"]` in the spec, so
 * a count or an id may arrive either way and every numeric field is coerced.
 */
type WireNumber = number | string;

interface WireTreeNode {
    id: WireNumber;
    name: string;
    descendant_count: WireNumber;
    has_children: boolean;
}

interface WireVector3 {
    x: number;
    y: number;
    z: number;
}

interface WireObject {
    /** Null only under `id_kind=external`, for a mapping the index cannot resolve. */
    id: WireNumber | null;
    path: string;
    name: string;
    level: WireNumber;
    has_children: boolean;
    bounds?: {
        box: { min: WireVector3; max: WireVector3 };
        sphere?: { center: WireVector3; radius: WireNumber } | null;
    } | null;
    properties?: [string, string][] | null;
}

interface WireComputedBounds {
    box?: { min: WireVector3; max: WireVector3 } | null;
    sphere?: { center: WireVector3; radius: WireNumber } | null;
}

function sceneVersionUrl(target: BimQueryTarget): string {
    return `/rest/v2.1/companies/${target.companyId}/projects/${target.projectId}/bim/scenes/${target.sceneId}/versions/${target.sceneVersionId}`;
}

function vec3(vector: WireVector3): ReadonlyVec3 {
    // The API sends `{x, y, z}` objects; everything downstream of here wants an array.
    return [vector.x, vector.y, vector.z];
}

/**
 * `ObjectData.bounds` carries both a box and a sphere, and the API's sphere is optional,
 * so one is derived from the box when it is absent — a consumer drawing or framing an
 * object should not have to know which half the index happened to store.
 */
function parseBounds(bounds: NonNullable<WireObject["bounds"]>): BimQueryBounds {
    const box = { min: vec3(bounds.box.min), max: vec3(bounds.box.max) };
    if (bounds.sphere) {
        return { box, sphere: { center: vec3(bounds.sphere.center), radius: Number(bounds.sphere.radius) } };
    }
    const center: ReadonlyVec3 = [
        (box.min[0] + box.max[0]) / 2,
        (box.min[1] + box.max[1]) / 2,
        (box.min[2] + box.max[2]) / 2,
    ];
    const radius = Math.hypot(box.max[0] - box.min[0], box.max[1] - box.min[1], box.max[2] - box.min[2]) / 2;
    return { box, sphere: { center, radius } };
}

/**
 * Deliberately not {@link parseBounds}: that derives a sphere from the box when the API
 * sends none, which is right for an object that has one volume expressed two ways, and
 * wrong here — an absent part was simply not asked for.
 */
function parseComputedBounds(bounds: WireComputedBounds): BimQueryComputedBounds {
    const computed: BimQueryComputedBounds = {};
    if (bounds.box) {
        computed.box = { min: vec3(bounds.box.min), max: vec3(bounds.box.max) };
    }
    if (bounds.sphere) {
        computed.sphere = { center: vec3(bounds.sphere.center), radius: Number(bounds.sphere.radius) };
    }
    return computed;
}

function parseTreeNode(node: WireTreeNode): BimQueryTreeNode {
    return {
        id: Number(node.id) as ObjectId,
        name: node.name,
        descendantCount: Number(node.descendant_count),
        hasChildren: node.has_children,
    };
}

function parseObject(object: WireObject): BimQueryObject | undefined {
    if (object.id === null) {
        return undefined;
    }
    return {
        id: Number(object.id) as ObjectId,
        path: object.path,
        name: object.name,
        level: Number(object.level),
        hasChildren: object.has_children,
        bounds: object.bounds ? parseBounds(object.bounds) : undefined,
        properties: object.properties ?? [],
    };
}

/**
 * The `cursor` of the response's `rel="next"` link, or `null` at the end of a collection.
 *
 * The tree reads are keyset-paged and carry their cursor in the `Link` header rather
 * than the body, so it is read here alongside the rows. Splitting on `,` is safe for
 * these links: the cursor is base64url and the rest of the URL is fixed by the route.
 */
function nextCursorFrom(response: Response): string | null {
    const header = response.headers.get("Link");
    if (!header) {
        return null;
    }
    for (const part of header.split(",")) {
        if (!/;\s*rel\s*=\s*"?next"?/i.test(part)) {
            continue;
        }
        const url = part.match(/<([^>]+)>/)?.[1];
        if (!url) {
            continue;
        }
        try {
            // Relative against the API host, since a link may arrive either way and only
            // the query string is wanted.
            return new URL(url, "https://api.procore.com").searchParams.get("cursor");
        } catch {
            return null;
        }
    }
    return null;
}

/** The `Total` header, when the collection reports one. */
function totalFrom(response: Response): number | undefined {
    const header = response.headers.get("Total");
    if (header === null) {
        return undefined;
    }
    const total = Number(header);
    return Number.isFinite(total) ? total : undefined;
}

/** core-http rejects non-2xx with an error carrying the status. */
function isNotFound(error: unknown): boolean {
    return (error as { status?: number } | null)?.status === 404;
}

/**
 * One object with its properties, or `undefined` when the id names nothing in this
 * scene version.
 */
export async function getBimQueryObject(
    target: BimQueryTarget,
    params: { id: ObjectId; signal?: AbortSignal },
): Promise<BimQueryObject | undefined> {
    try {
        const response = await procoreClient<Envelope<WireObject>>(`${sceneVersionUrl(target)}/objects/${params.id}`, {
            method: "GET",
            params: { id_kind: ID_KIND, view: "FULL" },
            parseResponse: throwWhenBackfilling<Envelope<WireObject>>((res) => res.json()),
            signal: params.signal,
        });
        return parseObject(response.data);
    } catch (e) {
        // A 404 is "no such object", which every caller here treats as an absence
        // rather than a failure — the same contract `ObjectDataAccess.getObject` states.
        if (isNotFound(e)) {
            return undefined;
        }
        throw e;
    }
}

/**
 * One page of a node's immediate children, and the cursor that continues it.
 *
 * `id` is a node id or {@link BIM_QUERY_ROOT} for the top of the tree. There is no
 * total: the engine counts a level only by walking it.
 */
export async function listBimQueryChildren(
    target: BimQueryTarget,
    params: {
        id: ObjectId | typeof BIM_QUERY_ROOT;
        cursor?: string | null;
        perPage?: number;
        signal?: AbortSignal;
    },
): Promise<{ items: BimQueryTreeNode[]; nextCursor: string | null }> {
    const perPage = params.perPage ?? BIM_QUERY_TREE_PAGE_LIMIT;
    return procoreClient<{ items: BimQueryTreeNode[]; nextCursor: string | null }>(
        `${sceneVersionUrl(target)}/objects/${params.id}/children`,
        {
            method: "GET",
            params: { id_kind: ID_KIND, per_page: perPage, ...(params.cursor ? { cursor: params.cursor } : {}) },
            parseResponse: throwWhenBackfilling(async (res) => {
                const body = (await res.json()) as Envelope<WireTreeNode[]>;
                const items = body.data.map(parseTreeNode);
                const nextCursor = nextCursorFrom(res);
                if (!nextCursor && items.length >= perPage) {
                    // A full page with nowhere to go next is the signature of a `Link`
                    // header that did not reach us — and there is no other way to ask
                    // for the rest of a keyset-paged level, so the level is silently
                    // short. Say so rather than passing it off as complete.
                    console.warn(
                        `[bim-query] a full page of children for ${params.id} carried no "next" link; the rest of this level cannot be paged`,
                    );
                }
                return { items, nextCursor };
            }),
            signal: params.signal,
        },
    );
}

/**
 * A node's ancestor chain, **nearest first** — the immediate parent leads and the
 * top-level node ends it. Not paged: it is bounded by the tree's depth, and the API
 * has no separate parent endpoint because the first element is that answer.
 */
export async function listBimQueryAncestors(
    target: BimQueryTarget,
    params: { id: ObjectId; signal?: AbortSignal },
): Promise<BimQueryTreeNode[]> {
    const response = await procoreClient<Envelope<WireTreeNode[]>>(
        `${sceneVersionUrl(target)}/objects/${params.id}/ancestors`,
        {
            method: "GET",
            params: { id_kind: ID_KIND },
            parseResponse: throwWhenBackfilling<Envelope<WireTreeNode[]>>((res) => res.json()),
            signal: params.signal,
        },
    );
    return response.data.map(parseTreeNode);
}

/**
 * One page of the ids beneath a node, transitively and **excluding the node itself**,
 * in ascending internal-id order. `total` is the whole subtree's size when the
 * collection reports it.
 */
export async function listBimQueryDescendantIds(
    target: BimQueryTarget,
    params: { id: ObjectId; page: number; perPage?: number; signal?: AbortSignal },
): Promise<{ ids: number[]; total: number | undefined }> {
    return procoreClient<{ ids: number[]; total: number | undefined }>(
        `${sceneVersionUrl(target)}/objects/${params.id}/descendants`,
        {
            method: "GET",
            // `view=ID` answers with bare ids rather than objects, which is all a caller
            // painting or hiding a subtree needs.
            params: {
                id_kind: ID_KIND,
                view: "ID",
                page: params.page,
                per_page: params.perPage ?? BIM_QUERY_OBJECT_PAGE_LIMIT,
            },
            parseResponse: throwWhenBackfilling(async (res) => {
                const body = (await res.json()) as Envelope<WireNumber[]>;
                return { ids: body.data.map(Number), total: totalFrom(res) };
            }),
            signal: params.signal,
        },
    );
}

/**
 * One page of the objects matching `predicate`, in ascending internal-id order — a
 * stable snapshot, so paging never repeats or skips a row.
 *
 * `degraded` says the query may have covered less than it was asked for, which the
 * engine reports as metadata on a `200` rather than as an error: the rows returned are
 * correct, but rows are missing with nothing to name them.
 */
export async function searchBimQueryObjects(
    target: BimQueryTarget,
    params: {
        predicate: SearchPredicate;
        scopeId?: ObjectId;
        page: number;
        perPage?: number;
        signal?: AbortSignal;
    },
): Promise<{ items: BimQueryObject[]; degraded: boolean }> {
    return procoreClient<{ items: BimQueryObject[]; degraded: boolean }>(`${sceneVersionUrl(target)}/objects/search`, {
        method: "POST",
        body: {
            predicate: params.predicate,
            ...(params.scopeId === undefined ? {} : { scope_id: String(params.scopeId) }),
        },
        // `LIGHT` carries id, path and name — a row, without the property read a
        // `FULL` page would cost for every match.
        params: {
            id_kind: ID_KIND,
            view: "LIGHT",
            page: params.page,
            per_page: params.perPage ?? BIM_QUERY_OBJECT_PAGE_LIMIT,
        },
        parseResponse: throwWhenBackfilling(async (res) => {
            const body = (await res.json()) as Envelope<WireObject[]>;
            const items = body.data.map(parseObject).filter((object): object is BimQueryObject => object !== undefined);
            return { items, degraded: body.meta?.degraded === true };
        }),
        signal: params.signal,
    });
}

/**
 * One page of the ids matching `predicate` — the same read as
 * {@link searchBimQueryObjects} at `view=ID`, for a caller that wants the match set and
 * not the rows.
 *
 * Separate rather than a `view` parameter because the response shape differs: bare ids
 * instead of objects, so nothing is composed and nothing can fail to parse.
 */
export async function searchBimQueryObjectIds(
    target: BimQueryTarget,
    params: {
        predicate: SearchPredicate;
        scopeId?: ObjectId;
        page: number;
        perPage?: number;
        signal?: AbortSignal;
    },
): Promise<{ ids: number[]; degraded: boolean }> {
    return procoreClient<{ ids: number[]; degraded: boolean }>(`${sceneVersionUrl(target)}/objects/search`, {
        method: "POST",
        body: {
            predicate: params.predicate,
            ...(params.scopeId === undefined ? {} : { scope_id: String(params.scopeId) }),
        },
        params: {
            id_kind: ID_KIND,
            view: "ID",
            page: params.page,
            per_page: params.perPage ?? BIM_QUERY_OBJECT_PAGE_LIMIT,
        },
        parseResponse: throwWhenBackfilling(async (res) => {
            const body = (await res.json()) as Envelope<WireNumber[]>;
            return { ids: body.data.map(Number), degraded: body.meta?.degraded === true };
        }),
        signal: params.signal,
    });
}

/**
 * One page of the union of the given ids' subtrees, deduplicated by the engine — one
 * request for a whole set where a per-id {@link listBimQueryDescendantIds} walk would be
 * one each.
 *
 * `degraded` reports a scan deadline hit: the ids returned are correct, but the set covers
 * fewer objects than the roots stand for.
 *
 * @param params.ids Roots to expand — at most {@link BIM_QUERY_EXPAND_ID_LIMIT} per request.
 * @param params.includeSelf Include the provided ids (root nodes). @default true.
 * @param params.perPage Defaults to {@link BIM_QUERY_OBJECT_PAGE_LIMIT}.
 */
export async function expandBimQueryObjectIds(
    target: BimQueryTarget,
    params: {
        ids: readonly number[];
        includeSelf?: boolean;
        page: number;
        perPage?: number;
        signal?: AbortSignal;
    },
): Promise<{ ids: number[]; degraded: boolean }> {
    return procoreClient<{ ids: number[]; degraded: boolean }>(`${sceneVersionUrl(target)}/objects/expand`, {
        // `POST` rather than the `QUERY` the API also routes this on: `QUERY` is the
        // verb whose read semantics are explicit, but it is undocumented on purpose and
        // needs a fallback, and core-http has no reason to send a verb intermediaries
        // may not pass.
        method: "POST",
        // A JSON array of numbers *is* the internal-id encoding — the element type names
        // it, so no `id_kind` is sent for the request (the one below names the response).
        body: { ids: [...params.ids] },
        params: {
            id_kind: ID_KIND,
            view: "ID",
            page: params.page,
            per_page: params.perPage ?? BIM_QUERY_OBJECT_PAGE_LIMIT,
            // Omitted rather than defaulted here, so the API's default stays the default.
            ...(params.includeSelf === undefined ? {} : { include_self: params.includeSelf }),
        },
        parseResponse: throwWhenBackfilling(async (res) => {
            const body = (await res.json()) as Envelope<WireNumber[]>;
            return { ids: body.data.map(Number), degraded: body.meta?.degraded === true };
        }),
        signal: params.signal,
    });
}

/**
 * The volume enclosing a set of objects — the engine's "zoom to selection" primitive,
 * folded over the index's synthetic `$bounds/*` columns in one native call.
 *
 * A fold over **exactly the ids given**: the endpoint's `scope_id` is what frames a
 * subtree, and this client does not send one, so a grouping node contributes only its
 * own geometry. `null` bounds means no member of the selection had any at all — which is
 * also what a scan deadline yields, since the engine never answers a partial volume — and
 * the caller's cue to leave the camera alone.
 *
 * Unpaged: the answer is one computed shape rather than a page of rows. The `ids` list
 * has no ceiling on the endpoint, so the client batches at {@link
 * BIM_QUERY_BOUNDS_ID_LIMIT} for the request-body reason given there, and the caller
 * folds the batches — which is sound, since an enclosing volume of the parts encloses the
 * whole.
 */
export async function getBimQueryBounds(
    target: BimQueryTarget,
    params: {
        ids: readonly number[];
        /** The API defaults to `BOX`; this client always states what it wants. */
        parts: BimQueryBoundsParts;
        signal?: AbortSignal;
    },
): Promise<{ bounds: BimQueryComputedBounds | null; degraded: boolean }> {
    return procoreClient<{ bounds: BimQueryComputedBounds | null; degraded: boolean }>(
        `${sceneVersionUrl(target)}/objects/bounds`,
        {
            // `POST` rather than the `QUERY` the API also routes this on, for
            // `expandBimQueryObjectIds`' reason.
            method: "POST",
            // As on `objects/expand`, a JSON array of numbers *is* the internal-id
            // encoding, so the `id_kind` below names only `scope_id` — which this client
            // never sends.
            body: { ids: [...params.ids], parts: params.parts },
            params: { id_kind: ID_KIND },
            parseResponse: throwWhenBackfilling(async (res) => {
                const body = (await res.json()) as Envelope<WireComputedBounds | null>;
                return {
                    bounds: body.data ? parseComputedBounds(body.data) : null,
                    degraded: body.meta?.degraded === true,
                };
            }),
            signal: params.signal,
        },
    );
}

/**
 * Whether this scene version has an index the API will answer queries from.
 *
 * Asks for one row of the tree's top level, which is the cheapest read that needs the
 * index to actually be mounted: a `200` means it is there, and everything else means it
 * is not usable *now* — a `202` while the service fetches it, a `404` for a scene
 * version that has none, a `403` for a caller who may not read it.
 *
 * Deliberately **not** wrapped in {@link throwWhenBackfilling}, unlike every other read
 * here. A probe whose answer is "fall back to the object DB" must not raise the
 * app-wide "preparing" banner on the way: the caller has another backend and the user
 * has nothing to wait for. Non-2xx still rejects, as core-http rejects everything
 * non-2xx, so a caller wanting a plain boolean catches.
 */
export async function probeBimQuery(target: BimQueryTarget, params?: { signal?: AbortSignal }): Promise<boolean> {
    return procoreClient<boolean>(`${sceneVersionUrl(target)}/objects/${BIM_QUERY_ROOT}/children`, {
        method: "GET",
        params: { id_kind: ID_KIND, per_page: 1 },
        parseResponse: (res) => res.status === 200,
        signal: params?.signal,
    });
}
